package dev.lingvo.site.components.studio.compose

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import dev.lingvo.shared.StorePlatform
import com.varabyte.kobweb.core.init.InitKobweb
import com.varabyte.kobweb.core.init.InitKobwebContext
import kotlinx.browser.document
import dev.lingvo.site.i18n.tr
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.Element
import org.w3c.dom.events.MouseEvent
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlinx.coroutines.cancel
import org.w3c.dom.events.Event
import dev.lingvo.site.i18n.t

/** Area identity pins the session generation so a new login cannot inherit an old run. */
data class ComposeRunKey(val project: String, val platform: StorePlatform, val session: Long)

/** Area-lifetime run owner; routes merely observe it and never own its coroutine. */
class ComposeRunStore private constructor(val key: ComposeRunKey) {
    var progress: ComposeProgress by mutableStateOf(ComposeProgress())
        private set
    private val scope: CoroutineScope = MainScope()
    private var task: Job? = null
    private val unload: (Event) -> Unit = { event ->
        if (progress.running) { event.preventDefault(); event.asDynamic().returnValue = "" }
    }

    private val leaveLink: (Event) -> Unit = { raw ->
        val event = raw as? MouseEvent
        if (event != null && event.button.toInt() == 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
            var element = event.target as? Element
            while (element != null && element !is HTMLAnchorElement) element = element.parentElement
            val anchor = element as? HTMLAnchorElement
            if (anchor != null && anchor.target != "_blank" && !anchor.hasAttribute("download") && !insideArea(anchor.pathname, key)) {
                if (!window.confirm(tr("studio.compose.compose_run_store.leave_screenshots_the_remaining_images_will", "Leave screenshots? The remaining images will not be composed."))) {
                    event.preventDefault()
                    event.stopImmediatePropagation()
                }
            }
        }
    }

    /** Starts only one run; authority is evaluated by the runner independently of route claims. */
    fun start(runner: ComposeRunner, jobs: List<ComposeJob>) {
        if (task?.isActive == true) return
        task = scope.launch {
            window.addEventListener("beforeunload", unload)
            document.addEventListener("click", leaveLink, true)
            try { runner.run(jobs) { progress = it } }
            finally { window.removeEventListener("beforeunload", unload); document.removeEventListener("click", leaveLink, true) }
        }
    }

    /** Cancels all work through structured cancellation, including fetch and XHR. */
    fun cancel() { task?.cancel() }

    companion object {
        private val stores = mutableMapOf<ComposeRunKey, ComposeRunStore>()
        /** Reuses the same run across Plan, Design and Review. */
        fun forArea(key: ComposeRunKey): ComposeRunStore {
            stores.keys.filter { it.session != key.session }.toList().forEach(::leaveArea)
            return stores.getOrPut(key) { ComposeRunStore(key) }
        }
        /** Leaving the screenshot area releases its owner after cancelling the active run. */
        fun leaveArea(key: ComposeRunKey) {
            stores.remove(key)?.let { store -> store.cancel(); store.scope.cancel() }
        }
        /** The app router also retires runs after leaving Plan or Design, where Review is no longer mounted. */
        fun retireForPath(path: String) {
            stores.keys.filter { !insideArea(path.substringBefore('?').substringBefore('#'), it) }.toList().forEach(::leaveArea)
        }
        /** Call on route disposal: the timer lets the router publish the destination first. */
        fun retireOutsideArea() {
            window.setTimeout({
                retireForPath(window.location.pathname)
            }, 0)
        }
    }
}

private fun encodeProjectSegment(value: String): String = js("encodeURIComponent(value)") as String

private fun insideArea(path: String, key: ComposeRunKey): Boolean {
    val prefix = "/app/projects/${encodeProjectSegment(key.project)}/screenshots"
    return path == prefix || path.startsWith("$prefix/")
}

/** Route interception supplies the area lifetime even when the Review composition has already left. */
@InitKobweb
fun initComposeRunLifetime(context: InitKobwebContext) {
    context.router.addRouteInterceptor { ComposeRunStore.retireForPath(path) }
    window.addEventListener("popstate", { ComposeRunStore.retireForPath(window.location.pathname) })
    window.addEventListener("pagehide", { ComposeRunStore.retireForPath("") })
}
